import { FC, useMemo, useState } from 'react';
import { Button, Col, Input, Row, Space, Switch, Typography } from 'antd';
import { TbCheck, TbCopy, TbExternalLink } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import { SmallTag } from '@/shared/components/tags';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';

interface ShareProps {
    component: ReportComponent;
}

const Share: FC<ShareProps> = ({ component }) => {
    const { token } = useAntDesignTheme();
    const updateReportComponent = useReportComponentStore((state) => state.updateReportComponent);
    const reportAccess = useReportComponentAccess(component);
    const [updating, setUpdating] = useState(false);
    const [copied, setCopied] = useState<'url' | 'iframe' | undefined>();
    const publicUrl = `${window.location.origin}/s/report-components/${component.id}`;
    const iframeCode = useMemo(
        () =>
            `<iframe src="${publicUrl}" width="100%" height="480" style="border:0;border-radius:8px;" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>`,
        [publicUrl],
    );

    const copy = async (value: string, target: 'url' | 'iframe') => {
        await navigator.clipboard.writeText(value);
        setCopied(target);
        window.setTimeout(() => setCopied(undefined), 1600);
    };

    const togglePublicAccess = async (publiclyVisible: boolean) => {
        if (!reportAccess.canUpdate || component.locked) {
            return;
        }

        setUpdating(true);

        try {
            await updateReportComponent(component.id, { publiclyVisible });
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div style={{ padding: '1rem' }}>
            <Space direction='vertical' size={16} style={{ width: '100%' }}>
                <Row align='middle' gutter={[12, 8]} wrap={false}>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Typography.Text strong>
                            Public
                            <SmallTag message={'<iframe />'} /> Embed
                        </Typography.Text>
                        <div>
                            <small style={{ color: token.colorTextTertiary }}>
                                Generate a public, read-only URL for this report component.
                            </small>
                        </div>
                    </Col>
                    <Col flex='none'>
                        <Switch
                            checked={Boolean(component.publiclyVisible)}
                            disabled={!reportAccess.canUpdate || component.locked}
                            loading={updating}
                            onChange={togglePublicAccess}
                        />
                    </Col>
                </Row>

                <div>
                    <small style={{ color: token.colorTextTertiary }}>Share URL</small>
                    <Input.Group compact style={{ display: 'flex', marginTop: '0.375rem' }}>
                        <Input value={publicUrl} readOnly disabled={!component.publiclyVisible} />
                        <Button
                            disabled={!component.publiclyVisible}
                            icon={copied === 'url' ? <TbCheck /> : <TbCopy />}
                            onClick={() => copy(publicUrl, 'url')}
                        />
                        <Button
                            disabled={!component.publiclyVisible}
                            icon={<TbExternalLink />}
                            onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                        />
                    </Input.Group>
                </div>

                <div>
                    <small style={{ color: token.colorTextTertiary }}>Iframe</small>
                    <Input.TextArea
                        value={iframeCode}
                        readOnly
                        disabled={!component.publiclyVisible}
                        autoSize={{ minRows: 4, maxRows: 6 }}
                        style={{ marginTop: '0.375rem', fontFamily: 'monospace' }}
                    />
                    <Button
                        disabled={!component.publiclyVisible}
                        icon={copied === 'iframe' ? <TbCheck /> : <TbCopy />}
                        style={{ marginTop: '0.5rem' }}
                        onClick={() => copy(iframeCode, 'iframe')}
                    >
                        Copy iframe
                    </Button>
                </div>
            </Space>
        </div>
    );
};

export default Share;
